import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { fromEvent, Observable, of, shareReplay } from 'rxjs';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { Platform } from '@angular/cdk/platform';

@Injectable({
  providedIn: 'root',
})
export class WindowRef {
  readonly document = inject<Document>(DOCUMENT);
  protected platformId = inject<Platform>(PLATFORM_ID);

  /**
   * Returns true when invoked in browser context.
   * Use this method to check if you can access `window` and other browser globals.
   */
  isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * Exposes global `window` object. In SSR when `window` is not available it returns `undefined`.
   * To detect if you can safely use `nativeWindow` use `isBrowser` to check execution platform.
   */
  get nativeWindow(): Window | undefined {
    return this.isBrowser() ? window : undefined;
  }

  /**
   * Exposes global `sessionStorage` object. In SSR when `sessionStorage` is not available it returns `undefined`.
   * To detect if you can safely use `sessionStorage` use `isBrowser` to check execution platform.
   */
  get sessionStorage(): Storage | undefined {
    return this.nativeWindow?.sessionStorage;
  }

  /**
   * Exposes global `localStorage` object. In SSR when `localStorage` is not available it returns `undefined`.
   * To detect if you can safely use `localStorage` use `isBrowser` to check execution platform.
   */
  get localStorage(): Storage | undefined {
    return this.nativeWindow?.localStorage;
  }

  /**
   * Returns the window/document location, undefined if document is not available (i.e. in SSR).
   */
  get location(): Partial<Location | undefined> {
    if (this.isBrowser()) {
      return this.document.location;
    } else {
      return undefined;
    }
  }

  /**
   * Returns an observable for the window resize event.
   * If there's no window object available (i.e. in SSR), a null value is emitted.
   */
  get resize$(): Observable<any> {
    if (!this.nativeWindow) {
      return of(null);
    } else {
      return fromEvent(window, 'resize').pipe(
        startWith({ target: this.nativeWindow }),
        distinctUntilChanged(),
        shareReplay(1),
      );
    }
  }

  setTimeout(callback: () => void, timeout = 0): number {
    if (this.isBrowser()) {
      return window.setTimeout(callback, timeout);
    } else {
      callback();
      return 0;
    }
  }

  /**
   * Replaces the current window location with a new one based on the given culture.
   */
  replaceWindowLocationForCulture(
    culture: string,
    navigateToHome = false,
  ): void {
    if (this.location && this.location.href) {
      let location = this.location.href
        .split('/')
        .map((item: string) =>
          item.match('^[a-z]{2}-[a-z]{2}$') ? culture : item,
        );
      if (navigateToHome)
        location = location.slice(0, location.indexOf(culture) + 1);
      this.location.replace?.(location.join('/'));
    }
  }

  /**
   * Remove Azure B2C Msal keys and value from storage
   */
  removeAuthKeyPairsFromSession() {
    const sessionStorage = this.sessionStorage;
    if (!sessionStorage) return;
    if (sessionStorage.length > 0) {
      const authKeys = ['b2clogin', 'msal', 'login'];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i) as string;
        if (authKeys.includes(key)) {
          sessionStorage.removeItem(key);
        }
      }
    }
  }
}
